import React, { useEffect, useRef, useState } from 'react';
import { Accordion, Form, ListGroup, Button } from 'react-bootstrap';
import { useCatSolr } from '../hooks/useCatSolr';
import THLSkeleton from './THLSkeleton';
import { useCatSettings } from '../hooks/useCatSettings';
import { useBiblStore } from '../hooks/useBiblStore';
import { SearchWylieInput } from '../search/NavBasicSearch';
import { EwtsConverter } from '../../views/TranslationTool/src/EwtsConverter.mjs';
import MandalaSkeleton from '../../views/common/MandalaSkeleton';
import Table from 'react-bootstrap/Table';
import { zfill } from './catalogUtils';
import {
    GoTriangleDown,
    GoTriangleRight,
    HiOutlineXCircle,
} from 'react-icons/all';
import { isTib } from '../../views/common/utils';
import Card from 'react-bootstrap/Card';
import { isStringColor } from 'ol/color';

function CatalogSettings(props) {
    const { collinfo } = useBiblStore();
    const selectRef = useRef(null);

    const colls = collinfo?.colls;

    const { targets, allSelected, chooseAllITXTargets, targString } =
        useCatSettings();
    const [colltargets, setCollTargets] = useState([]); // just ids
    const [collsShowing, setCollsShowing] = useState([]);

    const selectAllTargets = (doSelect = true) => {
        if (doSelect) {
            chooseAllITXTargets();
            setCollTargets([]);
            setCollsShowing([]);
            selectRef.current.selectedIndex = 0;
        } else {
            chooseAllITXTargets(false);
        }
    };
    const addCollection = (e) => {
        const selcoll = e.target.value;
        if (selcoll === 'all') {
            selectAllTargets();
        } else if (
            selcoll?.length > 0 &&
            selcoll in colls &&
            colls[selcoll]?.digeds?.length > 0
        ) {
            const newcoll = (
                <ItxCollectionSelector
                    key={`collsel-${selcoll}`}
                    coll={selcoll}
                    ed={colls[selcoll].digeds[0]}
                />
            );
            setCollTargets([newcoll, ...colltargets]);
            setCollsShowing([selcoll, ...collsShowing]);
            selectRef.current.selectedIndex = 0;
        }
    };

    useEffect(() => {
        // What am I trying to do here??? Makes Collection list window go away after blur and refocus.
        if (colls && 1 === 2) {
            const newseltargs = [];
            for (const [collsig, colltargs] of Object.entries(targets)) {
                const edsig = colls[collsig]?.digeds[0];
                if (edsig && colltargs[edsig]?.length > 0) {
                    const colltarg = (
                        <ItxCollectionSelector
                            key={`collsel-${collsig}`}
                            coll={collsig}
                            ed={edsig}
                        />
                    );
                    newseltargs.push(colltarg);
                }
            }
            setCollTargets(newseltargs);
        }
    }, [targets, colls]);

    if (!collinfo) {
        return null;
    }

    const digcolls = Object.keys(colls).map(
        (collkey) => colls[collkey].digeds.length > 0
    ); //colls.filter((coll) => coll.digeds.length > 0);

    return (
        <div>
            <h2>Intertextuality Targets</h2>
            <p>
                These are the target editions against which the current text is
                compared for intertextuality.{' '}
            </p>
            <p title="Target String" className="d-none">
                <strong>Parameters: </strong>
                <code>{targString}</code>
            </p>

            <CatalogITXSelectedTargets />

            {digcolls.length > 1 && (
                <div className="itx-target-chooser border-top pt-3 d-flex flex-column">
                    <div className="row">
                        <h3 className="col-8 h2">Choose a collection</h3>
                    </div>
                    <div className="itx-target-selector">
                        <div className="itx-target-selector-coll">
                            <label htmlFor={`collection-select`}>
                                Collection
                            </label>
                            <select
                                id={`collection-select`}
                                name={`collection-select`}
                                ref={selectRef}
                                onChange={addCollection}
                            >
                                <option value="" key="choose-coll-key">
                                    -- Select a Collection --
                                </option>
                                <option
                                    key="select-all-colls"
                                    value="all"
                                    disabled={allSelected()}
                                >
                                    All Collections
                                </option>
                                {collinfo.collsigs.map((sig, i) => {
                                    if (sig !== 'nogo') {
                                        return (
                                            <option
                                                key={[sig, i]}
                                                value={sig}
                                                disabled={collsShowing.includes(
                                                    sig
                                                )}
                                            >
                                                {colls[sig]?.title_en}
                                            </option>
                                        );
                                    }
                                })}
                            </select>
                        </div>
                    </div>
                </div>
            )}
            {/* colltargets is a registered list of <ItxCollectionSelector> components */}
            {colltargets.length > 0 && (
                <div className="itx-target-chooser border-top pt-3 d-flex flex-column">
                    {colltargets}
                </div>
            )}
        </div>
    );
}

/**
 * Shows targets already selected
 *
 * @returns {JSX.Element|null}
 * @constructor
 */
function CatalogITXSelectedTargets() {
    const { collinfo } = useBiblStore();
    const colls = collinfo?.colls;
    const { targets, removeITXTarget } = useCatSettings();

    if (!collinfo) {
        return null;
    }
    let isSelected = false;
    return (
        <div className="itx-selected-targets">
            <h3 className="h2">Selected Targets</h3>
            {Object.keys(targets).map((coll, i) => {
                const ed = Object.keys(targets[coll])[0]; // only one ed each
                if (Object.keys(targets[coll][ed])?.length > 0) {
                    isSelected = true;
                    const collkey = `${coll}-${ed}-targetlist`;
                    return (
                        <CatalogITXSelectedColl
                            coll={colls[coll]}
                            ed={ed}
                            key={collkey}
                        />
                    );
                } else {
                    return null;
                }
            })}
            {!isSelected && <p>No targets selected</p>}
        </div>
    );
}

function CatalogITXSelectedColl({ coll, ed }) {
    const { targets, isITXTarget, removeITXTarget } = useCatSettings();
    const csig = coll.id;
    const vollist = Object.keys(targets[csig][ed]);
    vollist.sort();
    const volsout = vollist
        .map((vnum, i) => {
            if (/^\d/.test(vnum)) {
                const item = targets[csig][ed][vnum];
                const mykey = `${csig}-${ed}-${vnum}-${i}-selcol`;
                return (
                    <CatalogITXSelectedVol
                        coll={csig}
                        ed={ed}
                        vnum={vnum}
                        data={item}
                        key={mykey}
                    />
                );
            }
        })
        .filter((item) => item !== null && item !== undefined);
    const allOfMe = isITXTarget(csig, ed);

    // const vollist = <li>Holding space for you</li>;
    const divkey = `${csig}-${ed}-targetlist`;
    const exceptstr = 'all' + (volsout.length > 0 ? ' except' : '') + ': ';
    //console.log('Vols out', volsout);

    const exceptel = allOfMe ? <span>{exceptstr}</span> : <span>: </span>;
    return (
        <div key={divkey}>
            <p>
                <strong>{coll?.title_en}</strong> ({ed}) {exceptel}
                <a className="link" onClick={() => removeITXTarget(csig, ed)}>
                    <HiOutlineXCircle />
                </a>
            </p>
            <ul>{volsout}</ul>
        </div>
    );
}

function CatalogITXSelectedVol({ coll, ed, vnum, data }) {
    const { removeITXTarget } = useCatSettings();
    const vstr = 'v' + String(parseInt(vnum));
    const dkeys = Object.keys(data);
    dkeys.sort();

    if (data['all'] === true) {
        const excludedTexts = dkeys
            .map((txtkey, i) => {
                const text = data[txtkey];
                const { title, exclude } = text;
                if (/^\d/.test(txtkey)) {
                    return (
                        <CatalogITXSelectedText
                            coll={coll}
                            ed={ed}
                            vnum={vnum}
                            txtkey={txtkey}
                            title={title}
                            key={`${coll}-${ed}-${vstr}-${txtkey}-except`}
                        />
                    );
                } else {
                    return null;
                }
            })
            .filter((item) => item !== null);
        const exceptstr = excludedTexts.length > 0 ? ' except: ' : '';
        return (
            <li key={`${coll}-${ed}-${vstr}-all`}>
                {vstr}: all {exceptstr}{' '}
                <a
                    className="link"
                    onClick={() => removeITXTarget(coll, ed, vnum)}
                >
                    <HiOutlineXCircle />
                </a>
                {excludedTexts.length > 0 && <ul>{excludedTexts}</ul>}
            </li>
        );
    }

    return dkeys.map((txtkey, i) => {
        const text = data[txtkey];
        if (/^\d/.test(txtkey)) {
            const { title, exclude } = text;
            return (
                <CatalogITXSelectedText
                    coll={coll}
                    ed={ed}
                    vnum={vnum}
                    txtkey={txtkey}
                    title={title}
                    key={`${coll}-${ed}-${vstr}-${txtkey}`}
                />
            );
        }
    });
}

function CatalogITXSelectedText({ coll, ed, vnum, txtkey, title }) {
    const { removeITXTarget } = useCatSettings();
    const vstr = 'v' + String(parseInt(vnum));
    return (
        <li>
            {vstr}.{txtkey}: {title}
            <a
                className="link"
                onClick={() => removeITXTarget(coll, ed, vnum, txtkey)}
            >
                <HiOutlineXCircle />
            </a>{' '}
        </li>
    );
}

/**
 * Displays a list of a collection's volumes and texts for choosing as targets
 *
 * @param coll
 * @param ed
 * @returns {JSX.Element}
 * @constructor
 */
function ItxCollectionSelector({ coll, ed }) {
    let { collinfo } = useBiblStore();
    const colls = collinfo?.colls;
    const { targets, addITXTarget } = useCatSettings();
    const [open, setOpen] = useState(true);

    const toggle = () => setOpen(!open);

    const triangle = open ? <GoTriangleDown /> : <GoTriangleRight />;

    return (
        <Accordion activeKey={open ? '0' : null}>
            <Accordion.Toggle
                as="div"
                variant="link"
                eventKey="0"
                onClick={toggle}
                className="d-flex align-items-center"
            >
                {triangle}
                <label>
                    Collection {colls[coll]?.title_en} ({coll}.{ed})
                </label>
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="0">
                <ItxVolumeSelectorPortal collsig={coll} edsig={ed} />
            </Accordion.Collapse>
        </Accordion>
    );
}

/**
 * Shows one volume at a time with a selector for choosing any other volume
 *
 * @param collsig
 * @param edsig
 * @returns {JSX.Element}
 * @constructor
 */
function ItxVolumeSelectorPortal({ collsig, edsig = 'pt' }) {
    const { targets, isITXTarget, addITXTarget } = useCatSettings(); //#b4bdbd
    const [volnum, setVolnum] = useState(0);
    const [txtfilter, setTxtfilter] = useState('');
    const ewts = new EwtsConverter();
    const titlesrcinput = useRef(null);
    const params = {
        q: `coll:${collsig} AND edsig:${edsig}`, //
        fq: ['type:tibbibl', `subtype:volume OR subtype:text`],
        fl: '*,[child]',
        rows: 10000,
        sort: 'vol_num asc,text_num asc',
    };

    const {
        isLoading: areItemsLoading,
        data: collItems,
        isError: isCollItemError,
        error: collItemError,
    } = useCatSolr(params);

    if (areItemsLoading) {
        return <MandalaSkeleton />;
    }

    if (isCollItemError) {
        console.log(collItemError);
        return <div>Coll item error!</div>;
    }

    const isAllCollSelected =
        isITXTarget(collsig, edsig) && targets[collsig][edsig]['all'] === true;

    const voldocs = collItems.docs.filter((d, i) => d.subtype === 'volume');
    let textdocs = collItems.docs.filter((d, i) => d.subtype === 'text');

    const showVol = (e) => {
        const val = e?.target?.value ?? e;
        setVolnum(val);
    };

    let timeout = null;

    const searchTexts = (e) => {
        const val = e.target.value;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            if (isTib(val)) {
                setTxtfilter(val);
            } else {
                setTxtfilter(ewts.to_unicode(val));
            }
        }, 500);
    };

    const vols = collItems.docs.filter((item, i) => {
        return item.subtype === 'volume';
    });

    /*
    const volitems = collItems.docs.filter((item, i) => {
        return item?.vol_num === volnum * 1 && item.subtype === 'text';
    });*/

    const filteredItems =
        txtfilter?.length > 0
            ? collItems.docs.filter((item, i) => {
                  return (
                      item.titles.filter((t) => {
                          if (!t?.title_bo || t.title_bo?.length === 0)
                              return false;
                          return t?.title_bo[0]?.includes(txtfilter);
                      }).length > 0
                  );
              })
            : [];
    // console.log('Filtered items', filteredItems);

    const volclass = filteredItems?.length > 0 ? 'd-none' : '';

    return (
        <div className="itx-target-selector-table">
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <Table bordered hover className="selector">
                    <thead>
                        <tr>
                            <th>
                                <select
                                    name="volchoice"
                                    onChange={showVol}
                                    className={volclass}
                                >
                                    <option
                                        key={`${collsig}-${edsig}-vol-all`}
                                        value={0}
                                    >
                                        All
                                    </option>
                                    {vols.map((item, i) => (
                                        <option
                                            key={`${collsig}-${edsig}-vol-choice-${i}`}
                                            value={item?.vol_num * 1}
                                        >
                                            {item?.vol_num}
                                        </option>
                                    ))}
                                </select>
                            </th>
                            <th>
                                <strong>Search:</strong>
                                <input
                                    key={`titlesrchstr-${collsig}-${edsig}`}
                                    type="text"
                                    name="titlesrchstr"
                                    ref={titlesrcinput}
                                    onChange={searchTexts}
                                    // checked={selectedVolumes.includes(volnum)}
                                />
                                {!isAllCollSelected && (
                                    <div className="whole-coll-btn d-inline-block pl-lg-3 text-right">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() =>
                                                addITXTarget(
                                                    collsig,
                                                    edsig,
                                                    'all',
                                                    'All texts in this collection'
                                                )
                                            }
                                        >
                                            Add Whole Collection
                                        </button>
                                    </div>
                                )}
                            </th>
                        </tr>
                        {isAllCollSelected && (
                            <tr>
                                <th colSpan={2}>
                                    <div className="alert alert-info">
                                        <strong>Note:</strong> This whole
                                        collection has been selected. Select
                                        volumes and/or texts to be excluded.
                                    </div>
                                </th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {filteredItems.length > 0 &&
                            filteredItems.map((item, i) => {
                                return (
                                    <ItxTextSelector
                                        key={`filtered-text--${i}`}
                                        item={item}
                                        collSel={isAllCollSelected}
                                    />
                                );
                            })}
                        {filteredItems.length === 0 &&
                            voldocs.map((vol, i) => {
                                const voltexts = textdocs.filter((text, i) => {
                                    return text?.vol_num === vol?.vol_num;
                                });
                                const vnum_i = volnum * 1;
                                if (
                                    vnum_i === vol.vol_num * 1 ||
                                    vnum_i === 0
                                ) {
                                    return (
                                        <ItxVolumeSelector
                                            key={`vol-${i}`}
                                            vol={vol}
                                            texts={voltexts}
                                            collSelected={isAllCollSelected}
                                        />
                                    );
                                }
                                return null;
                            })}
                    </tbody>
                </Table>
            </form>
        </div>
    );
}

/**
 * An individual volume of a collection shown in the volume portal to choose targets from
 *
 * @param vol
 * @param texts
 * @returns {JSX.Element}
 * @constructor
 */
function ItxVolumeSelector({ vol, texts, collSelected }) {
    const { targets, isITXTarget, addITXTarget, removeITXTarget } =
        useCatSettings();
    const [isSel, setIsSel] = useState(false);
    const coll = vol.coll;
    const ed = vol.edsig;
    const volnum = vol.vol_num;
    const volnumid = zfill(volnum, 3);
    const volelid = `${coll}-${ed}-v${volnumid}-row`;
    const volclass = 'vol';

    const addVol = () => {
        const voladd = collSelected ? `-${volnum}` : `${volnum}`;
        console.log('in add vol', isSel);
        if (isSel) {
            removeITXTarget(coll, ed, voladd, 'all');
            setIsSel(false);
        } else {
            console.log('adding itx target', coll, ed, voladd);
            addITXTarget(coll, ed, voladd, 'all', `Volume ${volnum}`);
            setIsSel(true);
        }
    };

    useEffect(() => {
        // console.log('Targets changed', targets);
        setIsSel(isITXTarget(coll, ed, volnum));
    }, [targets]);

    const selverb = collSelected ? 'Exclude' : 'Select';

    return (
        <>
            <tr
                key={`vol-${volnum}-key`}
                id={volelid}
                className={'volhead ' + volclass}
            >
                <td>
                    <strong>Vol. {volnum}</strong>
                </td>
                <td className="text-right font-90">
                    {selverb} whole volume
                    <input
                        key={`vol-${volnum}-select`}
                        type="checkbox"
                        checked={isSel}
                        value={'v' + volnum}
                        onChange={addVol}
                    />
                    {isSel && !collSelected && (
                        <div>(Click on a text in this volume to remove it)</div>
                    )}
                </td>
            </tr>
            {texts.map((item, i) => {
                return (
                    <ItxTextSelector
                        item={item}
                        volsel={isSel}
                        collsel={collSelected}
                        key={item.id + '-' + i.toString()}
                    />
                );
            })}
        </>
    );
}

/**
 * An individual text within a volume to select as a target
 *
 * @param item
 * @param volsel
 * @returns {JSX.Element}
 * @constructor
 */
function ItxTextSelector({ item, volsel = false, collsel = false }) {
    const { targets, addITXTarget, isITXTarget } = useCatSettings();
    const collsig = item.coll;
    const edsig = item.edsig;
    const volnum = item.vol_num;
    const vnum = zfill(volnum, 3);
    const volexcluded = collsel && volsel;
    const txtnum = item.text_num;
    let tnum = zfill(txtnum.toString(), 4);

    const iAmTarget = isITXTarget(collsig, edsig, vnum, tnum);
    let textclass =
        iAmTarget || volexcluded || volsel ? 'chosen cursor-default' : '';

    if (volsel) {
        textclass += ' clickable';
    }

    if (iAmTarget && volsel) {
        textclass = '';
    }

    /*if (tnum === '0003') {
        console.log('0003 Is target:', isITXTarget(collsig, edsig, vnum, tnum));
        console.log(
            'volexclude: ',
            volexcluded,
            'volse: ',
            volsel,
            'collsel: ',
            collsel
        );
        console.log("Text class: " + textclass);
    }*/

    const selectme = (e, eorb) => {
        e.preventDefault();
        // const tnum = zfill(eorb?.text_num, 4);
        // console.log('Item in text selector', item);
        // console.log('volsel is: ', volsel);
        const texttitle =
            eorb?.titles[0]?.title[0]?.length > 0
                ? eorb.titles[0].title[0]
                : '';
        let vnum = eorb?.vol_num;
        if (collsel) {
            vnum = '-' + vnum;
        }
        if (volsel || collsel) {
            tnum = '-' + tnum;
        }
        addITXTarget(collsig, edsig, vnum, tnum, texttitle);
    };

    //const classval = isSelected ? 'chosen' : '';
    let title = 'No Title Recorded';
    if (
        item?.titles &&
        item?.titles?.length > 0 &&
        item.titles[0]?.title_bo &&
        item.titles[0].title_bo.length > 0
    ) {
        title = item.titles[0].title_bo[0];
    }

    const tid = `${collsig}-${edsig}-t${tnum}-title`;

    return (
        <tr
            key={`vol-item-${volnum}-${txtnum}`}
            onClick={(e) => {
                if (textclass === '' || volsel) {
                    // text class exists when either selected or disabled
                    selectme(e, item);
                }
            }}
            className={textclass}
        >
            <td>{item?.text_num}</td>
            <td id={tid}>{title}</td>
        </tr>
    );
}

export default CatalogSettings;
